const MAX_DIMENSION = 2048
const JPEG_QUALITY = 0.85
const SKIP_THRESHOLD = 1024 * 1024 // 1MB — imagens menores nao comprimem
const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

function isImageType(type: string): boolean {
  return IMAGE_TYPES.includes(type)
}

function calcDimensions(
  width: number,
  height: number,
  maxDim: number,
): { width: number; height: number } {
  if (width <= maxDim && height <= maxDim) return { width, height }

  const ratio = Math.min(maxDim / width, maxDim / height)
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  }
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Falha ao carregar imagem'))
    }

    img.src = url
  })
}

function renderToCanvas(
  img: HTMLImageElement,
  width: number,
  height: number,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext('2d')!
  // Melhora qualidade de redimensionamento
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, 0, 0, width, height)

  return canvas
}

function canvasToFile(
  canvas: HTMLCanvasElement,
  originalName: string,
  quality: number,
): Promise<File | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null)
          return
        }
        const name = originalName.replace(/\.\w+$/, '.jpg')
        resolve(new File([blob], name, { type: 'image/jpeg' }))
      },
      'image/jpeg',
      quality,
    )
  })
}

/**
 * Comprime imagem para envio ao servidor.
 * - Redimensiona para max 2048px no maior lado (qualidade suficiente para OCR/IA)
 * - Converte para JPEG 85%
 * - Pula PDFs e imagens < 1MB
 * - Se compressao nao reduzir tamanho, retorna original
 */
export async function compressImageFile(file: File): Promise<File> {
  if (!isImageType(file.type)) return file
  if (file.size < SKIP_THRESHOLD) return file

  try {
    const img = await loadImage(file)
    const { width, height } = calcDimensions(img.width, img.height, MAX_DIMENSION)

    // Se dimensoes ja estao dentro do limite e arquivo < 2MB, nao comprimir
    if (width === img.width && height === img.height && file.size < 2 * 1024 * 1024) {
      return file
    }

    const canvas = renderToCanvas(img, width, height)
    const compressed = await canvasToFile(canvas, file.name, JPEG_QUALITY)

    if (!compressed || compressed.size >= file.size) return file

    return compressed
  } catch {
    // Em caso de erro, envia original
    return file
  }
}
